import React, { useEffect, useRef, useState } from 'react';
import { FiAlertTriangle, FiCheck } from 'react-icons/fi';
import { useAuthStore } from '../../stores';
import { useApi } from '../../hooks/useApi';
import { showToast } from '../../utils/toast';
import { LoadingButton } from './Loader';

const SECTIONS = [
  {
    title: '1. Educational & Software Service Only',
    body: `TradePilot is a software, educational, and informational service designed to provide market analysis tools, trade activity, copy-trading technology, automation assistance, and algorithm-based trade identification. TradePilot is not a bank, brokerage, investment advisor, financial institution, law firm, or licensed financial advisory service. The platform is intended for informational and educational purposes only.`,
  },
  {
    title: '2. No Guarantee of Profits or Performance',
    body: `The User understands and acknowledges that: no profits, returns, or financial gains are guaranteed; no trade, signal, alert, or algorithm prediction is guaranteed to be accurate; past performance does not guarantee future results; financial markets are volatile and unpredictable; users may lose part or all of their invested capital. TradePilot does not guarantee winning trades, profitable outcomes, account growth, or protection from losses.`,
  },
  {
    title: '3. Market Risk Disclosure',
    body: `Trading stocks, options, forex, cryptocurrencies, futures, and other financial instruments involves substantial risk. The User understands that market prices fluctuate rapidly and unpredictably due to economic events, news, liquidity, volatility, earnings reports, geopolitical events, market conditions, and other factors outside of TradePilot's control. TradePilot does not control the financial markets and cannot prevent losses.`,
  },
  {
    title: '4. Copy Trading & Algorithmic Tools',
    body: `TradePilot may provide copy-trading technology, automated trade replication tools, algorithm-based alerts, or trade activity visibility. Any copied trade, algorithm-generated signal, or market prediction is based solely on potential market movement identified through software analysis and trading activity. TradePilot does not guarantee that trades will be profitable, that trades will execute at the same prices, that trades will be suitable for every account size or risk tolerance, that copied trades will match master account performance, or that losses will be avoided.`,
  },
  {
    title: '5. User Responsibility',
    body: `The User understands and agrees that they are 100% responsible for all trading decisions, account activity, profits, losses, taxes, brokerage fees, and financial outcomes related to their account. The User is solely responsible for determining how much money to risk, choosing whether to place or copy trades, managing their brokerage account, understanding the risks associated with trading, and monitoring their own financial situation. The User agrees never to risk more money than they can afford to lose.`,
  },
  {
    title: '6. No Financial, Legal, or Investment Advice',
    body: `TradePilot does not provide financial advice, investment advice, legal advice, tax advice, accounting advice, or fiduciary services. Nothing provided through the platform, Discord, Telegram, software, social media, webinars, customer support, videos, messages, or educational materials should be interpreted as a recommendation to buy or sell securities, a guarantee of future performance, personalized investment advice, or a legal or financial representation. Users should consult licensed financial professionals before making financial decisions.`,
  },
  {
    title: '7. Limitation of Liability',
    body: `TradePilot, its owners, affiliates, staff, contractors, traders, moderators, software providers, and representatives shall not be held liable for trading losses, loss of profits or capital, brokerage execution issues, slippage, delayed orders, market volatility, platform outages, algorithmic inaccuracies, third-party brokerage actions, or any financial damages or losses. The User acknowledges that all trading activity is performed at their own risk.`,
  },
  {
    title: '8. No Refunds for Market Performance',
    body: `The User understands that refunds will not be granted based solely on trading losses, market downturns, unprofitable trades, algorithm performance, account drawdowns, or dissatisfaction with market results. Refund requests, if any, may be reviewed on a case-by-case basis regarding billing or service-related issues only.`,
  },
  {
    title: '9. Electronic Acceptance',
    body: `By selecting "I Agree," subscribing, accessing the platform, or using TradePilot services, the User acknowledges and agrees that electronic acceptance is legally binding and carries the same effect as a physical signature.`,
  },
];

const RiskDisclosureAgreementModal: React.FC = () => {
  const setUser = useAuthStore((s) => s.setUser);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const acceptMutation = useApi.useAcceptRiskAgreement({
    onSuccess: (data) => {
      if (data.user) setUser(data.user);
      showToast('success', 'Agreement accepted.');
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      showToast('error', err.response?.data?.error || 'Failed to save agreement. Please try again.');
    },
  });

  useEffect(() => {
    const el = contentRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 50) {
      setHasScrolledToBottom(true);
    }
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const canAgree = hasScrolledToBottom && !acceptMutation.isPending;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-md">
      <div className="w-full max-w-3xl max-h-[90vh] sm:max-h-[92vh] flex flex-col rounded-2xl border border-[#2a3142] bg-[#12161f] shadow-2xl shadow-black/50 overflow-hidden">
        <header className="shrink-0 border-b border-[#2a3142] px-4 sm:px-6 py-4 sm:py-5 bg-[#151a24]">
          <div className="flex items-start gap-3 sm:gap-3.5">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-400">
              <FiAlertTriangle className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-400/90 mb-1">
                Required agreement
              </p>
              <h2 className="text-lg sm:text-2xl font-bold text-white leading-tight tracking-tight break-words">
                TradePilot Trading Risk Disclosure &amp; User Agreement
              </h2>
              <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">
                You must read and accept this agreement to continue using TradePilot.
              </p>
            </div>
          </div>
        </header>

        <div
          ref={contentRef}
          onScroll={handleScroll}
          className="relative flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5 space-y-3.5 break-words"
        >
          <div className="rounded-xl border border-[#2a3142] bg-[#0e1219] p-4">
            <p className="text-sm text-slate-300 leading-relaxed">
              By selecting &ldquo;I Agree,&rdquo; using the TradePilot platform, accessing signals, copy-trading
              features, automation tools, market analysis, software, or any related services, the user
              (&ldquo;User&rdquo;) acknowledges that they have read, understood, and agreed to the terms outlined
              below.
            </p>
          </div>

          {SECTIONS.map((section) => (
            <div
              key={section.title}
              className="rounded-xl border border-[#2a3142] bg-[#151a24] p-4"
            >
              <h3 className="font-bold text-white mb-2 text-[15px]">{section.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{section.body}</p>
            </div>
          ))}

          <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.08] p-4">
            <h3 className="font-bold text-amber-300 mb-2 text-[15px]">User Acknowledgment</h3>
            <p className="text-sm text-amber-100/80 leading-relaxed">
              By selecting &ldquo;I Agree,&rdquo; the User confirms that they understand trading involves
              substantial financial risk, that losses may occur, and that they are fully responsible for all
              trading decisions and financial outcomes associated with their account.
            </p>
          </div>

          {!hasScrolledToBottom && (
            <div className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-8 pb-2 pointer-events-none">
              <p className="text-center text-sm text-slate-400 py-2.5 rounded-lg border border-[#2a3142]/80 bg-[#12161f]/95 backdrop-blur-sm">
                Scroll to the bottom to enable &ldquo;I Agree&rdquo;
              </p>
            </div>
          )}
        </div>

        <footer className="shrink-0 border-t border-[#2a3142] px-4 sm:px-6 py-3 sm:py-4 bg-[#151a24]">
          <LoadingButton
            type="button"
            onClick={() => acceptMutation.mutate()}
            disabled={!canAgree}
            isLoading={acceptMutation.isPending}
            className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold transition-all ${
              canAgree
                ? 'bg-[#3b82f6] hover:bg-[#2563eb] text-white shadow-lg shadow-blue-500/25'
                : 'bg-[#1a2030] border border-[#2a3142] text-slate-500 cursor-not-allowed'
            }`}
          >
            <FiCheck className="h-5 w-5" />
            I Agree
          </LoadingButton>
        </footer>
      </div>
    </div>
  );
};

export default RiskDisclosureAgreementModal;
